import { useEffect } from 'react';
import { convertDateToStringFormat } from '@bloomays-lib/utils.shared';
import { GridColDef, gridClasses, GridValueGetter } from '@mui/x-data-grid-premium';
import sortBy from 'lodash/fp/sortBy';
import { format } from 'date-fns/format';
import { fr } from 'date-fns/locale/fr';
import { styled } from '@mui/material/styles';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { SingleLineText } from '@bloomays-lib/ui.shared';
import { Title, LoaderSkeleton } from '@bloomays-lib/ui.shared';
import { NotificationHistoryGQL } from '@bloomays-lib/types.shared';
import { GET_NOTIFICATION_CRA_HISTORY } from '@bloomays-lib/adapter.api-bloomer';

import { errorLogger } from '../../helpers/error';
import { notify } from '../../helpers/toastify';
import RawDataGrid from '../molecules/DataGrid';

export type ChronologicalHistoryProps = {
  activityId: string | null;
  missionId: string;
  month: Date;
  play?: boolean;
};

const ContainerHistory = styled('div')(({ theme }) => ({
  textAlign: 'left',
  margin: '2em 0',
}));

const DataContainer = (props: { data: NotificationHistoryGQL[] }) => {
  const { data } = props;
  if (data.length === 0) return <SingleLineText text={"Pas d'historique de notifications pour ce CRA !"} />;

  const columns: GridColDef<NotificationHistoryGQL>[] = [
    {
      field: 'processedOn',
      headerName: "Date d'envoi",
      width: 150,
      valueFormatter: (value: Date) => convertDateToStringFormat(value, 'dd/MM/yyyy HH:mm'),
    },
    {
      field: 'type',
      headerName: 'Type de notification',
      width: 150,
      valueGetter: (value: GridValueGetter<NotificationHistoryGQL>, row: NotificationHistoryGQL) => {
        if (row.data.type === 'missingCRA') return 'Relance Bloomer';
        if (row.data.type === 'newLink') return 'Relance Client';
        if (row.data.type === 'signClient') return row.data.title;
        return row.data.type;
      },
    },
    {
      field: 'contact',
      headerName: 'Envoyé à',
      width: 200,
      valueGetter: (value: GridValueGetter<NotificationHistoryGQL>, row: NotificationHistoryGQL) => row.data.contact,
    },
    {
      field: 'progress',
      headerName: 'Statut',
      width: 150,
      valueFormatter: (value: number) => (value === 100 ? 'Envoyé 🎉' : 'Pas envoyé ❌'),
    },
    {
      field: 'error',
      headerName: "Raison de l'échec",
      width: 300,
    },
  ];

  return <DataGrid columns={columns} rows={data} />;
};

const ChronologicalHistory = ({ activityId, play = true, month, missionId }: ChronologicalHistoryProps) => {
  const { t } = useTranslation('craHistory');

  const { loading, data, error } = useQuery<{
    getNotificationCraHistory: NotificationHistoryGQL[];
  }>(GET_NOTIFICATION_CRA_HISTORY, {
    variables: {
      id: activityId,
      month: convertDateToStringFormat(month),
      missionId: missionId,
    },
    context: { clientName: 'api.bloomers' },
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (error) {
      errorLogger(error, {
        extraInfos: 'Internal server error, impossible to retrieve jobs data !',
      });
      notify('error', "Oups, j'ai pas réussi à récupérer l'historique, ré-essaye plus tard ! 😓", error);
    }
  }, [error]);

  const craMonthLabel = format(month, 'MMMM yyyy', { locale: fr });
  const title = t('title');
  const subtitle = t('subtitle', { month: craMonthLabel });

  return (
    <div>
      <Title textTitle={title} span={subtitle} />
      <br />
      {loading && (
        <LoaderSkeleton play={play} height={'80px'} width={'100%'} label={"Chargement de l'historique ..."} />
      )}
      {!loading && !error && (
        <ContainerHistory>
          <DataContainer data={sortBy('processedOn', data?.getNotificationCraHistory || [])} />
        </ContainerHistory>
      )}
      {error && (
        <ContainerHistory>
          <SingleLineText text="🚧 Error de récuperation de données" />
          <SingleLineText text={`Message : ${error}`} />
        </ContainerHistory>
      )}
    </div>
  );
};

export default ChronologicalHistory;
export { ChronologicalHistory };

const DataGrid = styled(RawDataGrid)(({ theme }) => ({
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.neutral[10],
  },
}));
